import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Calendar } from "../ui/calendar";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { overtimeAPI } from "../../lib/api";
import { toDateKey, parseDateKey, to12h, formatHours } from "../../lib/overtime";
import { Plus, Clock, History } from "lucide-react";
import { toast } from "sonner";

export function OvertimeCalendar({ entries, onChanged }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [form, setForm] = useState({ start_time: "", end_time: "", note: "" });
  const [saving, setSaving] = useState(false);

  const selectedKey = toDateKey(selectedDate);
  const dayEntries = entries.filter((e) => e.date === selectedKey);
  const markedDates = [...new Set(entries.map((e) => e.date))].map(parseDateKey);

  const handleAdd = async () => {
    if (!form.start_time || !form.end_time) {
      toast.error("Enter both start and end time");
      return;
    }
    setSaving(true);
    try {
      await overtimeAPI.addEntry({
        date: selectedKey,
        start_time: form.start_time,
        end_time: form.end_time,
        note: form.note,
      });
      toast.success("Overtime added");
      setForm({ start_time: "", end_time: "", note: "" });
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add overtime");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">My Overtime Calendar</CardTitle>
          <CardDescription>Days with overtime are highlighted. Select a day to view or add entries.</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(d) => d && setSelectedDate(d)}
            modifiers={{ hasOvertime: markedDates }}
            modifiersClassNames={{ hasOvertime: "bg-blue-100 text-blue-800 font-semibold rounded-full" }}
            data-testid="overtime-calendar"
          />
        </CardContent>
      </Card>

      <div className="space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Plus className="w-4 h-4 text-blue-600" />
              Add Overtime — {selectedDate.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Start Time</Label>
                <Input
                  type="time"
                  className="mt-1"
                  value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  data-testid="overtime-start-time"
                />
              </div>
              <div>
                <Label className="text-sm">End Time</Label>
                <Input
                  type="time"
                  className="mt-1"
                  value={form.end_time}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                  data-testid="overtime-end-time"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm">Note (optional)</Label>
              <Input
                className="mt-1"
                placeholder="What did you work on?"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                data-testid="overtime-note"
              />
            </div>
            <Button onClick={handleAdd} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="overtime-add-button">
              {saving ? "Saving..." : "Add Overtime"}
            </Button>
            <p className="text-xs text-slate-500">Entries are locked once added — contact Tanuj if a correction is needed.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-500" />
              Entries on this day
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2" data-testid="overtime-day-entries">
            {dayEntries.length === 0 ? (
              <p className="text-sm text-slate-500">No overtime recorded on this day.</p>
            ) : (
              dayEntries.map((entry) => <OvertimeEntryCard key={entry.id} entry={entry} />)
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function OvertimeEntryCard({ entry }) {
  const [showHistory, setShowHistory] = useState(false);
  const edited = (entry.edit_history || []).length > 0;
  return (
    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid={`overtime-entry-${entry.id}`}>
      <div className="flex items-center justify-between">
        <div>
          <span className="font-medium text-slate-800">
            {to12h(entry.start_time)} – {to12h(entry.end_time)}
          </span>
          <Badge className="ml-2 bg-blue-100 text-blue-700 hover:bg-blue-100">{formatHours(entry.hours)}</Badge>
        </div>
        {edited && (
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700"
            data-testid={`overtime-edited-badge-${entry.id}`}
          >
            <History className="w-3 h-3" /> Edited
          </button>
        )}
      </div>
      {entry.note && <p className="text-xs text-slate-600 mt-1">{entry.note}</p>}
      {edited && showHistory && (
        <div className="mt-2 pt-2 border-t border-slate-200 space-y-1" data-testid={`overtime-edit-history-${entry.id}`}>
          {entry.edit_history.map((h, i) => (
            <div key={`${h.edited_date}-${i}`} className="text-xs text-slate-600">
              <span className="font-medium">{h.edited_by}</span> on {new Date(h.edited_date).toLocaleString("en-AU")}:
              {h.changes.map((c) => (
                <span key={c.field} className="ml-1">
                  {c.field.replace("_", " ")}: <span className="line-through text-red-500">{String(c.old ?? "—")}</span> → <span className="text-emerald-600">{String(c.new)}</span>;
                </span>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
